/**
 * Client-side sanitization helpers - a defense-in-depth safety net over the
 * server, which already sanitizes owner-authored announcement HTML.
 * These are intentionally lightweight (no third-party dependency): they
 * neutralize the obvious script vectors and dangerous URL schemes without
 * stripping the legitimate rich HTML (headings, lists, links, emphasis)
 * customers rely on.
 */

// URL schemes that can execute script or smuggle markup when used as a link
// target or resource. Leading whitespace/control chars are tolerated because
// browsers strip them before parsing the scheme (e.g. a leading tab or NUL).
// eslint-disable-next-line no-control-regex
const DANGEROUS_URL_SCHEME = /^[\u0000-\u0020]*(?:javascript|data|vbscript):/i;

/**
 * Return the URL if it uses a safe scheme, or an empty string when it uses a
 * dangerous one (javascript:, data:, vbscript:). Relative URLs, http(s), mailto
 * and tel are all considered safe and returned unchanged.
 */
export const sanitizeUrl = (url: string | null | undefined): string => {
    if (!url) {
        return '';
    }

    return DANGEROUS_URL_SCHEME.test(url) ? '' : url;
};

/**
 * Strip the obvious script vectors from an HTML fragment: `<script>` elements,
 * inline event-handler attributes (on*), and dangerous URL schemes on href/src.
 * Everything else is preserved so rich formatting still renders.
 *
 * Parsing happens inside a detached <template>, whose contents are inert (no
 * scripts run, no resources load) until re-serialized - so the returned string
 * is safe to assign to innerHTML.
 */
export const sanitizeHtml = (html: string): string => {
    if (!html) {
        return '';
    }

    if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
        // No DOM available (SSR); apply a textual floor for the script vector.
        return html.replace(/<script[\s\S]*?<\/script\s*>/gi, '');
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    sanitizeFragment(template.content);
    return template.innerHTML;
};

const URL_ATTRIBUTES = new Set(['href', 'src', 'xlink:href']);

function sanitizeFragment(root: DocumentFragment): void {
    root.querySelectorAll('*').forEach((element) => {
        if (element.tagName === 'SCRIPT') {
            element.remove();
            return;
        }

        Array.from(element.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();

            if (name.startsWith('on')) {
                element.removeAttribute(attr.name);
            } else if (URL_ATTRIBUTES.has(name) && DANGEROUS_URL_SCHEME.test(attr.value)) {
                element.removeAttribute(attr.name);
            }
        });
    });
}
